import type { MetaFunction } from '@remix-run/node';
import React from 'react';
import { Link } from '@remix-run/react';
import { motion } from 'framer-motion';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '~/components/ui/accordion';
import { H1, H2, H3, Caption, Lead, Body } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';
import { staggerContainer, staggerItem } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'About Lumora',
    description:
      'Learn about the vision behind Lumora: The First Dawn and the studio crafting its world.',
  });

export default function AboutPage() {
  const { getVariants } = useAnimationState();

  return (
    <Container size="narrow" padding="large" className="space-y-16">
      {/* Game Overview Section */}
      <motion.section
        className="space-y-6 opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants(staggerContainer)}
      >
        <motion.header className="space-y-3" variants={getVariants(staggerItem)}>
          <Caption className="text-primary">About</Caption>
          <H1>The World of Lumora</H1>
          <Lead>
            Lumora: The First Dawn is a narrative-driven RPG about emerging magic in a world that has
            never known such power. This page will evolve with lore, development milestones, and the
            story of the studio.
          </Lead>
        </motion.header>
        <motion.div className="space-y-4" variants={getVariants(staggerItem)}>
          <Body className="text-muted-foreground">
            The Lumora universe is crafted with care, featuring dynamic combat, deep character
            progression, and a living world shaped by player decisions. Expect to find detailed lore,
            character profiles, and behind-the-scenes development updates here.
          </Body>
          <Body className="text-muted-foreground">
            While we build out the full overview, explore the{' '}
            <Link to={routes.blog.index()} className="text-primary underline hover:text-primary/80">
              development blog
            </Link>{' '}
            for the latest insights.
          </Body>
        </motion.div>
      </motion.section>

      {/* Roadmap Section */}
      <motion.section
        id="roadmap"
        className="scroll-mt-20 space-y-6 opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants({
          ...staggerContainer,
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.1,
              delayChildren: 0.3,
            },
          },
        })}
      >
        <motion.header className="space-y-3" variants={getVariants(staggerItem)}>
          <H2>Development Roadmap</H2>
          <Body className="text-muted-foreground">
            Our journey from concept to launch, tracking major milestones and planned features.
          </Body>
        </motion.header>
        <motion.div className="space-y-6" variants={getVariants(staggerItem)}>
          <div className="border-l-4 border-accent pl-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <H3>Core Systems</H3>
                <span className="rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                  In Progress
                </span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Combat mechanics and skill system</li>
                <li>• Character progression and attributes</li>
                <li>• World building tools and map editor</li>
                <li>• Database and content management</li>
              </ul>
            </div>
          </div>

          <div className="border-l-4 border-border pl-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <H3>Content Development</H3>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                  Planned
                </span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Main story arc and quest system</li>
                <li>• Enemy encounters and boss battles</li>
                <li>• Items, equipment, and crafting</li>
                <li>• World lore and character backstories</li>
              </ul>
            </div>
          </div>

          <div className="border-l-4 border-border pl-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <H3>Polish & Launch</H3>
                <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                  Future
                </span>
              </div>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Visual effects and audio design</li>
                <li>• Performance optimization</li>
                <li>• Beta testing and community feedback</li>
                <li>• Platform deployment and release</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        id="faq"
        className="scroll-mt-20 space-y-6 opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants({
          ...staggerContainer,
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.1,
              delayChildren: 0.5,
            },
          },
        })}
      >
        <motion.header className="space-y-3" variants={getVariants(staggerItem)}>
          <H2>Frequently Asked Questions</H2>
          <Body className="text-muted-foreground">
            Common questions about Lumora: The First Dawn and its development.
          </Body>
        </motion.header>
        <motion.div variants={getVariants(staggerItem)}>
          <Accordion type="multiple" defaultValue={[]}>
            <AccordionItem value="platforms">
              <AccordionTrigger>What platforms will Lumora be available on?</AccordionTrigger>
              <AccordionContent>
                Lumora is being developed for PC (Steam) with plans for macOS support. Mobile versions for iOS and Android are also in consideration for post-launch.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="release">
              <AccordionTrigger>When will the game be released?</AccordionTrigger>
              <AccordionContent>
                We're currently in active development and will announce a release timeline once core systems are complete. Follow the{' '}
                <Link to={routes.blog.index()} className="text-primary underline hover:text-primary/80">
                  development blog
                </Link>{' '}
                for the latest updates.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="unique">
              <AccordionTrigger>What makes Lumora unique?</AccordionTrigger>
              <AccordionContent>
                Lumora combines a hybrid 2D/3D aesthetic with deep character customization, real-time strategic combat, and a rich narrative world. Our focus is on meaningful player choices and emergent storytelling.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="support">
              <AccordionTrigger>Can I support the development?</AccordionTrigger>
              <AccordionContent>
                The best way to support us right now is to follow our progress, share feedback, and spread the word. We'll announce opportunities for playtesting and early access when available.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="multiplayer">
              <AccordionTrigger>Is there multiplayer or co-op?</AccordionTrigger>
              <AccordionContent>
                Lumora: The First Dawn is designed as a single-player experience focused on narrative depth and player agency. We're concentrating on delivering a polished solo adventure first.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </motion.section>
    </Container>
  );
}
